import { useVistassistantMail } from "@/contexts/VistassistantMailContext";
import AgendaItem from "@/domain/entities/agenda/AgendaItem";
import AgendaTimeSlot from "@/domain/entities/agenda/AgendaTimeSlot";
import Email, { MailsListId } from "@/domain/entities/emails/Email";
import type { AssistantAnswer as Answer } from "@/domain/entities/vistassistant/AIResponse";
import MailSource from "@/domain/enums/MailSource";
import { splitAnswer } from "@cs_teamwaydev/mailvista-sdk_react";
import { CalendarDays, Clock, Info, Mail, Send } from "lucide-react";
import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import AgendaTimeSlotsResponse, {
    formatTimeSlot,
} from "./AgendaTimeSlotsResponse";
import ChatEventList from "./ChatEventList";
import ChatMailList from "./ChatMailList";
import MailDraftCard from "./MailDraftCard";
import { mailPeople, peopleLabel } from "./mailPeople";
import ModelMarkdown from "./ModelMarkdown";
import QuestionCard from "./QuestionCard";

// A ref within a sentence: a light lavender chip with a thin border, so it reads as an element on the
// chat's near-white canvas (the former grey, #F5F5F8 on #F8F7FB, hardly showed). The Focus purples.
const chipClassName =
    "mx-0.5 inline-flex max-w-[16rem] items-center gap-1 rounded-md border border-TypeMail-FocusMedium bg-TypeMail-FocusLight px-1.5 align-baseline text-[13px] font-medium leading-snug text-primary-darker [&_svg]:size-3 [&_svg]:flex-none";
// A chip that opens or picks something: a shade deeper under the pointer.
const chipHoverClassName =
    "hover:border-TypeMail-FocusMediumHover hover:bg-TypeMail-FocusMedium";

type Kind = "mails" | "events" | "slots";

/** What a ref stands for, by its letter: [m3] a mail, [e2] an event, [c1] a free slot. */
const KINDS: Record<string, Kind> = { m: "mails", e: "events", c: "slots" };

const kindOf = (ref: string): Kind | undefined => KINDS[ref[0]];

/** Consecutive refs of the same kind, together: one list each. */
const groupByKind = (
    refs: string[],
): { kind: Kind | undefined; refs: string[] }[] =>
    refs.reduce<{ kind: Kind | undefined; refs: string[] }[]>((groups, ref) => {
        const kind = kindOf(ref);
        const last = groups[groups.length - 1];
        if (last && last.kind === kind) last.refs.push(ref);
        else groups.push({ kind, refs: [ref] });
        return groups;
    }, []);

const present = <T,>(record: Record<string, T>, refs: string[]): T[] =>
    refs
        .map((ref) => record[ref])
        .filter((item): item is T => item !== undefined);

/** The mails of the answer the user sent himself (step-based contract, « envoyes » box). */
const sentMailsOf = (answer: Answer): ReadonlySet<Email> =>
    new Set(present(answer.emails, answer.sentRefs ?? []));

/** Lines of refs only: the elements in full, as the lists of the app show them. */
const AnswerElements = ({
    answer,
    refs,
    listId,
    isPast,
}: {
    answer: Answer;
    refs: string[];
    listId: MailsListId;
    isPast: boolean;
}) => (
    <>
        {groupByKind(refs).map((group) => {
            const key = group.refs.join(",");
            if (group.kind === "mails") {
                const mails = present(answer.emails, group.refs);
                if (mails.length > 0)
                    return (
                        <ChatMailList
                            key={key}
                            listId={listId}
                            mails={mails}
                            sentMails={sentMailsOf(answer)}
                        />
                    );
            }
            if (group.kind === "events") {
                const events = present(answer.events, group.refs);
                if (events.length > 0)
                    return <ChatEventList key={key} events={events} />;
            }
            if (group.kind === "slots") {
                const shown = group.refs.filter((ref) => answer.slots[ref]);
                if (shown.length > 0)
                    return (
                        <AgendaTimeSlotsResponse
                            key={key}
                            isPast={isPast}
                            replies={shown.map(
                                (ref) => answer.slotReplies?.[ref],
                            )}
                            timeSlots={shown.map((ref) => answer.slots[ref])}
                        />
                    );
            }
            // A ref the conversation never read: it stays text.
            return (
                <p key={key}>{group.refs.map((ref) => `[${ref}]`).join(" ")}</p>
            );
        })}
    </>
);

/** A mail cited within a sentence: its sender, or for a mail the user sent, « À … » its recipients. */
const MailChip = ({
    mail,
    listId,
    sent,
}: {
    mail: Email;
    listId: MailsListId;
    sent: boolean;
}) => {
    const { t } = useTranslation();
    const { onMailClick } = useVistassistantMail();
    const names = peopleLabel(mailPeople(mail, sent));
    const people = sent && names ? t("vistassistant.sentTo", { names }) : names;
    const subject = mail.subject || t("vistassistant.noSubject");

    return (
        <button
            className={`${chipClassName} ${chipHoverClassName}`}
            onClick={() =>
                onMailClick?.(mail, sent ? MailSource.SentItems : listId)
            }
            title={people ? `${people} · ${subject}` : subject}
            type="button"
        >
            {sent ? <Send aria-hidden /> : <Mail aria-hidden />}
            <span className="truncate">{people || subject}</span>
        </button>
    );
};

const EventChip = ({ event }: { event: AgendaItem }) => {
    const { t, i18n } = useTranslation();
    const title = event.subject || t("vistassistant.untitledEvent");
    const when = new Date(event.start).toLocaleDateString(i18n.language, {
        weekday: "short",
        day: "numeric",
        month: "short",
    });

    return (
        <span className={chipClassName} title={`${title} · ${when}`}>
            <CalendarDays aria-hidden />
            <span className="truncate">{title}</span>
        </span>
    );
};

/** A free slot cited within a sentence; offered by the assistant (`reply`), a click picks it. */
const SlotChip = ({
    slot,
    reply,
}: {
    slot: AgendaTimeSlot;
    reply?: string;
}) => {
    const { i18n } = useTranslation();
    const { onAnswerQuestion } = useVistassistantMail();
    const content = (
        <>
            <Clock aria-hidden />
            <span className="truncate">
                {formatTimeSlot(
                    new Date(slot.startDate),
                    new Date(slot.endDate),
                    i18n.language,
                )}
            </span>
        </>
    );

    return reply && onAnswerQuestion ? (
        <button
            className={`${chipClassName} ${chipHoverClassName}`}
            onClick={() => onAnswerQuestion(reply)}
            type="button"
        >
            {content}
        </button>
    ) : (
        <span className={chipClassName}>{content}</span>
    );
};

interface AssistantAnswerProps {
    answer: Answer;
    /** The list of the mails the answer cites, for the mail opened from it. */
    listId: MailsListId;
    /** A later message exists: a question of this answer is past. */
    isPast?: boolean;
    chosenReply?: string;
}

/**
 * The assistant's answer, one per turn (step-based contract): its text, where each ref it cites
 * becomes the element the app holds for it — a line of refs only shows the elements in full (the
 * compact lists of the thread), a ref within a sentence a small chip. Only these elements are
 * clickable; anything else the model wrote is simple formatting (ModelMarkdown). A mail draft comes
 * after the text, in plain text (MailDraftCard); a question, with its choices (QuestionCard).
 */
const AssistantAnswer = ({
    answer,
    listId,
    isPast = false,
    chosenReply,
}: AssistantAnswerProps) => {
    const { t } = useTranslation();
    // Stable while the answer is: ModelMarkdown (memoized) parses its text again only when it changes.
    const renderRef = useCallback(
        (ref: string) => {
            const mail = answer.emails[ref];
            if (mail)
                return (
                    <MailChip
                        listId={listId}
                        mail={mail}
                        sent={answer.sentRefs?.includes(ref) ?? false}
                    />
                );
            const event = answer.events[ref];
            if (event) return <EventChip event={event} />;
            const slot = answer.slots[ref];
            if (slot)
                return (
                    <SlotChip
                        reply={isPast ? undefined : answer.slotReplies?.[ref]}
                        slot={slot}
                    />
                );
            return undefined;
        },
        [answer, listId, isPast],
    );
    const segments = useMemo(() => splitAnswer(answer.text), [answer.text]);

    return (
        <div className="flex animate-opacity flex-col gap-3 text-base leading-6 text-primary-darkest">
            {answer.unknownNames?.map((unknown) => (
                // A name the contacts did not find: said by the app, from its lookup, before the answer it
                // qualifies (PJ, 15/10); not read aloud.
                <p
                    key={`${unknown.role}:${unknown.name}`}
                    className="flex items-start gap-1.5 text-[13px] leading-snug text-assistant-muted"
                >
                    <Info aria-hidden className="mt-px size-4 flex-none" />
                    <span>
                        {t(`vistassistant.unknownName.${unknown.role}`, {
                            name: unknown.name,
                        })}
                    </span>
                </p>
            ))}
            {segments.map((segment, index) =>
                segment.kind === "markdown" ? (
                    <div key={index} className="markdown-content">
                        <ModelMarkdown renderRef={renderRef}>
                            {segment.text}
                        </ModelMarkdown>
                    </div>
                ) : (
                    <AnswerElements
                        key={index}
                        answer={answer}
                        isPast={isPast}
                        listId={listId}
                        refs={segment.refs}
                    />
                ),
            )}
            {answer.draft && (
                <MailDraftCard draft={answer.draft} listId={listId} />
            )}
            {answer.question && (
                <QuestionCard
                    chosenReply={chosenReply}
                    hideQuestion={
                        answer.text.trim() === answer.question.question
                    }
                    isPast={isPast}
                    question={answer.question}
                />
            )}
        </div>
    );
};

export default AssistantAnswer;
